import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  palettes,
  THEME_PREFERENCES,
  THEME_STORAGE_KEY,
  TOKEN_NAMES,
  type Palette,
  type ThemeMode,
  type ThemePreference,
} from './tokens.js';

/**
 * Applies the Blue Partner design tokens as CSS custom properties, and owns the
 * light/dark/system preference behind them.
 *
 * **This used to say there was no light mode, deliberately, and that has
 * changed** — see **ADR-0064**. The plumbing this file's own comment predicted
 * would be needed ("give this provider a `mode` prop; the plumbing already
 * carries a mode through") is what arrived; `daylightPalette` is the palette,
 * and the General settings panel is the switch.
 *
 * Three things live here rather than in the app, and each is here because
 * splitting it would let two things disagree about the current theme:
 *
 * - **The preference**, persisted to `localStorage` under one key. A theme that
 *   forgets itself on reload is not a setting, it is a toggle.
 * - **The resolution of `system`**, via `prefers-color-scheme`, *including the
 *   listener*. The OS can change while the app is open — that is the whole
 *   difference between `system` and picking a side — so this is a subscription,
 *   not a read at mount.
 * - **The write to the document**: the variables, `data-theme`, and
 *   `color-scheme`, so the UA chrome (scrollbars, form controls, caret) follows
 *   without per-element styling.
 *
 * Components do not need this provider to render correctly — `token()` emits the
 * command value as an inline `var()` fallback, so an unwrapped component renders
 * dark. The provider exists so the variables are set once on the document root
 * and so a subtree can be re-themed.
 */

export interface ThemeContextValue {
  /** What was chosen. `system` here means the palette below was inferred. */
  preference: ThemePreference;
  /** The palette actually in force. Never `system`. */
  mode: ThemeMode;
  /** The resolved palette, for the rare case a value is needed in JS. */
  palette: Palette;
  /** Choose a theme. Persists, and takes effect immediately. */
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

function isPreference(value: string | null): value is ThemePreference {
  return value !== null && (THEME_PREFERENCES as readonly string[]).includes(value);
}

/**
 * The stored preference, or `null`.
 *
 * Wrapped in a `try` because `localStorage` throws rather than returning
 * undefined when a browser blocks storage — Safari in private mode, and any
 * origin with third-party cookies disabled that ends up partitioned. A theme is
 * not worth a blank screen.
 */
function readStoredPreference(): ThemePreference | null {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : null;
  } catch {
    return null;
  }
}

function storePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // See `readStoredPreference`. The theme still applies for this session.
  }
}

/**
 * What the OS is asking for. `dark` when we cannot tell — jsdom implements no
 * `matchMedia` at all, and the command room is what this product looked like
 * for nine milestones, so it is the right answer to "no information".
 */
function systemMode(): ThemeMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
  return window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light';
}

/**
 * Subscribe to the OS preference changing under us.
 *
 * Paired with `systemMode` above through `useSyncExternalStore` rather than
 * `useState` + `useEffect`, and that is not a style choice: the media query is
 * an external store, and the hook that exists for external stores closes the gap
 * between "read at render" and "listener attached in an effect" without a
 * setState in an effect body to re-sync it. Returns a no-op unsubscribe where
 * there is nothing to subscribe to.
 */
function subscribeToSystemMode(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }

  const query = window.matchMedia(DARK_MEDIA_QUERY);
  query.addEventListener('change', onChange);
  return () => {
    query.removeEventListener('change', onChange);
  };
}

/** SSR has no media query to ask. Same answer as "no information" above. */
function serverSystemMode(): ThemeMode {
  return 'dark';
}

function applyPalette(palette: Palette, mode: ThemeMode, target: HTMLElement): void {
  for (const name of TOKEN_NAMES) {
    target.style.setProperty(`--${name}`, palette[name]);
  }
  target.dataset['theme'] = mode;
  // Tell the UA which way round this surface is so scrollbars, form controls,
  // and the caret follow without per-element styling.
  target.style.colorScheme = mode;
}

export interface ThemeProviderProps {
  children: ReactNode;
  /**
   * Element to write CSS variables onto. Defaults to `document.documentElement`.
   * Override to scope a theme to a subtree — useful in tests and previews.
   */
  target?: HTMLElement | null;
  /**
   * What to use when nothing has been stored for this origin.
   *
   * **Defaults to `dark`, not to `system`**, and that default is load-bearing:
   * `apps/admin` and every test that renders a provider pass nothing, and none
   * of them asked for a theme that changes with the operator's OS. `apps/web`
   * passes `system` explicitly, which is the opt-in.
   */
  defaultPreference?: ThemePreference;
}

export function ThemeProvider({
  children,
  target = null,
  defaultPreference = 'dark',
}: ThemeProviderProps) {
  /*
    Read once, lazily, rather than on every render: `localStorage` is not a
    React store, and the only thing that changes it in this tab is `choose`
    below, which sets the state itself.
  */
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => readStoredPreference() ?? defaultPreference,
  );
  /*
    Subscribed unconditionally rather than only while `preference === 'system'`.
    One listener, whose value nothing reads unless the preference is `system` —
    and gating the subscription on the preference would mean switching *to*
    `system` renders one frame against a stale answer.
  */
  const resolvedSystem = useSyncExternalStore(subscribeToSystemMode, systemMode, serverSystemMode);

  const mode: ThemeMode = preference === 'system' ? resolvedSystem : preference;
  const palette = palettes[mode];

  useEffect(() => {
    if (typeof document === 'undefined') return;
    applyPalette(palette, mode, target ?? document.documentElement);
  }, [palette, mode, target]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    storePreference(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, mode, palette, setPreference }),
    [preference, mode, palette, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Read the active theme, and change it.
 *
 * Throws outside a `ThemeProvider`. Components that merely *consume* tokens need
 * no provider at all — `token()` carries a fallback — so reach for this only
 * when a palette value is genuinely needed in JavaScript, or when the component
 * is the switch.
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === null) {
    throw new Error('useTheme must be used within a <ThemeProvider>.');
  }
  return context;
}
